<template>
    <div class="opinion">
        <div class="section-block">
            <div class="section-title">认定条款</div>
            <div class="clause-wrapper">
                <div v-if="legalClauseLoading" class="clause-loading">正在加载法定条款...</div>
                <template v-else>
                    <el-checkbox-group v-if="legalClauseOptions.length" v-model="form.legalClauses" class="clause-group">
                        <el-row :gutter="24">
                            <el-col
                                v-for="item in legalClauseOptions"
                                :key="item.dictValue"
                                :span="12"
                                class="clause-col"
                            >
                                <el-checkbox :label="String(item.dictValue)" class="clause-checkbox">
                                    <span class="clause-text">{{ item.dictLabel }}</span>
                                </el-checkbox>
                            </el-col>
                        </el-row>
                    </el-checkbox-group>
                    <div v-else class="clause-empty">暂无法定条款数据</div>
                </template>
            </div>
            <el-form :model="form" label-width="120px" class="section-form clause-form">
                <el-form-item label="条款类型">
                    <el-input
                        :value="clauseDecisionText"
                        placeholder="请先选择法定条款"
                        disabled
                    />
                </el-form-item>
                <el-form-item label="认定日期" prop="recognitionDate">
                    <el-date-picker
                        v-model="form.recognitionDate"
                        type="date"
                        placeholder="请选择认定日期"
                        value-format="YYYY-MM-DD HH:mm:ss"
                        style="width: 100%;"
                    />
                </el-form-item>
                <!-- 选中「放弃」时展示 -->
                <template v-if="isAbandonSelected">
                    <el-form-item label="放弃类型">
                        <el-select
                            v-model="form.abandonType"
                            placeholder="请选择放弃类型"
                            clearable
                            style="width: 100%;"
                        >
                            <el-option
                                v-for="item in abandonTypeOptions"
                                :key="item.dictValue"
                                :label="item.dictLabel"
                                :value="item.dictValue"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="放弃时间">
                        <el-date-picker
                            v-model="form.abandonTime"
                            type="datetime"
                            placeholder="请选择放弃时间"
                            value-format="YYYY-MM-DD HH:mm:ss"
                            style="width: 100%;"
                        />
                    </el-form-item>
                    <el-form-item v-if="showAbandonReason" label="放弃原因">
                        <el-input
                            v-model="form.abandonReason"
                            type="textarea"
                            :rows="3"
                            placeholder="请输入放弃原因"
                            resize="vertical"
                        />
                    </el-form-item>
                    <el-form-item label="被告人">
                        <el-input
                            v-model="form.defendant"
                            placeholder="请输入被告人"
                            clearable
                        />
                    </el-form-item>
                </template>
            </el-form>
        </div>

        <div class="section-block">
            <div class="section-title">调查核实情况</div>
            <el-form :model="form" label-width="120px" class="section-form">
                <el-form-item label="调查核实情况">
                    <el-input
                        v-model="form.accidentProcess"
                        type="textarea"
                        :rows="4"
                        placeholder="请输入调查核实情况"
                        resize="vertical"
                    />
                </el-form-item>
            </el-form>
        </div>

        <div class="section-block section-actions">
            <el-button type="primary" plain @click="handleSave">保存认定意见</el-button>
            <el-button type="primary" @click="handleSubmit">提交认定意见</el-button>
        </div>
    </div>
</template>

<script>
import { getListByDictType } from '@/api/sys/dictData'


export default {
    name: 'RecognitionOpinion',
    data() {
        return {
            form: {
                legalClauses: [],
                caseSummary: '',
                difficultPoints: '',
                recognitionDate: '',
                reviewOpinion: '',
                accidentProcess: '',
                accidentConclusion: '',
                remark: '',
                abandonType: '',
                abandonTime: '',
                abandonReason: '',
                defendant: ''
            },
            legalClauseOptions: [],
            legalClauseLoading: false,
            abandonTypeOptions: []
        }
    },
    created() {
        this.fetchLegalClauseOptions()
        this.fetchAbandonTypeOptions()
    },
    computed: {
        // 条款类型：多选时优先级 放弃 > 不予认定为工伤 > 予以视同为工伤(8-10) > 予以认定为工伤(1-7)
        clauseDecisionText() {
            if (!this.form.legalClauses || !this.form.legalClauses.length) {
                return ''
            }
            const selected = this.form.legalClauses.map((val) => String(val))
            let hasAbandon = false
            let hasRange1_7 = false
            let hasRange8_10 = false
            let hasOther = false
            for (const item of this.legalClauseOptions) {
                if (!selected.includes(String(item.dictValue))) continue
                const label = String(item.dictLabel || '').trim()
                if (label === '放弃') {
                    hasAbandon = true
                    break
                }
                const num = parseInt(item.dictValue, 10)
                if (!Number.isNaN(num)) {
                    if (num >= 1 && num <= 7) hasRange1_7 = true
                    else if (num >= 8 && num <= 10) hasRange8_10 = true
                    else hasOther = true
                } else {
                    hasOther = true
                }
            }
            if (hasAbandon) return '放弃'
            if (hasOther) return '不予认定为工伤'
            if (hasRange8_10) return '予以视同为工伤'
            if (hasRange1_7) return '予以认定为工伤'
            return ''
        },
        isAbandonSelected() {
            if (!this.form.legalClauses || !this.form.legalClauses.length) return false
            const selected = this.form.legalClauses.map((val) => String(val))
            return this.legalClauseOptions.some(
                (item) => selected.includes(String(item.dictValue)) && String(item.dictLabel || '').trim() === '放弃'
            )
        },
        // 放弃类型为「诈骗放弃」时显示放弃原因
        showAbandonReason() {
            if (!this.form.abandonType) return false
            const opt = this.abandonTypeOptions.find(
                (item) => String(item.dictValue) === String(this.form.abandonType)
            )
            return opt && String(opt.dictLabel || '').trim() === '诈骗放弃'
        }
    },
    methods: {
        async fetchLegalClauseOptions() {
            this.legalClauseLoading = true
            try {
                const res = await getListByDictType('industrial_injury_recognition_clause_type')
                if (res && res.code === 200 && Array.isArray(res.data)) {
                    this.legalClauseOptions = res.data
                } else {
                    this.legalClauseOptions = []
                }
            } catch (error) {
                console.warn('法定条款数据加载失败', error)
                this.legalClauseOptions = []
            } finally {
                this.legalClauseLoading = false
            }
        },
        async fetchAbandonTypeOptions() {
            try {
                const res = await getListByDictType('industrial_injury_recognition_abandon_type')
                if (res && res.code === 200 && Array.isArray(res.data)) {
                    this.abandonTypeOptions = res.data
                } else {
                    this.abandonTypeOptions = []
                }
            } catch (error) {
                console.warn('放弃类型字典加载失败', error)
                this.abandonTypeOptions = []
            }
        },
        handleSave() {
            this.$message.info('保存认定意见（接口待对接）')
        },
        handleSubmit() {
            this.$message.info('提交认定意见（接口待对接）')
        }
    }
}
</script>

<style lang="scss" scoped>
.opinion {
    width: 100%;
    height: 100%;
}

.section-block {
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }
}

.section-title {
    position: relative;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    padding-left: 11px;
    margin-bottom: 12px;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 12px;
        border-radius: 20px;
        background: var(--vt-c-blue);
    }
}

.clause-wrapper {
    padding: 16px 20px;
    border: 1px dashed rgba(41, 125, 238, 0.4);
    border-radius: 6px;
    background-color: #f8fbff;
}

.clause-loading,
.clause-empty {
    font-size: 13px;
    color: #909399;
}

.clause-group {
    width: 100%;
}

.clause-col {
    margin-bottom: 12px;
}

.clause-checkbox {
    width: 100%;
    background: #F7F7F7;
    border-radius: 4px;
    padding: 10px;
    box-sizing: border-box;
    :deep(.el-checkbox__label) {
        white-space: normal;
        line-height: 20px;
        color: #303133;
    }
}

.clause-text {
    display: inline-block;
    font-size: 14px;
}

.clause-form {
    margin-top: 12px;
}

.section-form {
    :deep(.el-form-item__label) {
        color: #606266;
        font-weight: 400;
    }
}

.section-actions {
    padding-bottom: 24px;
    .el-button {
        margin-right: 12px;
    }
}
</style>